import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import ServiceDaysStrip from "../components/ServiceDaysStrip";
import MapPickerModal from "../components/MapPickerModal";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { browserGeocode } from "../lib/browserGeocode";
import CustomerGroupsManager, { GroupBadge, GroupMultiSelect } from "../components/CustomerGroupsManager";
import CustomerBillingPanel from "../components/customers/CustomerBillingPanel";
import PortalAccessPanel from "../components/customers/PortalAccessPanel";
import { Plus, PencilSimple, Trash, MagnifyingGlass, X, MapPin as MapPinIcon, NavigationArrow, Copy, Tag, CaretDown, Lock } from "@phosphor-icons/react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CONTAINER_SIZES = [2, 4, 6, 8];
const WASTE_STREAMS = ["MSW", "Single Stream Recycling", "Cardboard", "Yard Waste", "C&D", "Other"];
const FREQS = [
  { v: "weekly", l: "Weekly (4.33/mo)" },
  { v: "biweekly", l: "Bi-Weekly (2.17/mo)" },
  { v: "monthly", l: "Monthly (1/mo)" },
  { v: "twice_weekly", l: "Twice Weekly (8.66/mo)" },
  { v: "three_weekly", l: "3x / Week (12.99/mo)" },
];

function newContainer() {
  return {
    // Client-side stable key so React doesn't lose row state when a container is removed.
    // Server-supplied containers carry their server `id` instead.
    _key: `c_${Math.random().toString(36).slice(2)}_${Date.now()}`,
    waste_stream: "MSW",
    container_size: 4,
    quantity: 1,
    service_frequency: "weekly",
    service_days: [],
    route_id: "",
    driver_notes: "",
    // Optional per-can drop location. Leave blank to inherit the customer's
    // main service_address. Backend geocodes on save so the map shows a pin
    // for each can even when they sit at different addresses.
    service_address: "",
    lat: null,
    lng: null,
    // Itemized recurring revenue. `monthly_price` (the customer total) is
    // computed from these live in the UI and re-derived server-side.
    service_price: 0,
    fuel_surcharge: 0,
    franchise_fee: 0,
    other_fees: [],
    monthly_price: 0,
    status: "active",
    last_serviced_at: null,
  };
}

/** Sum a container's itemized fees. Used everywhere the "monthly total" is displayed. */
function computeContainerTotal(ct) {
  const svc = Number(ct?.service_price ?? ct?.monthly_price ?? 0) || 0;
  const fuel = Number(ct?.fuel_surcharge || 0) || 0;
  const fran = Number(ct?.franchise_fee || 0) || 0;
  const other = (ct?.other_fees || []).reduce((s, f) => s + (Number(f?.amount) || 0), 0);
  return Math.round((svc + fuel + fran + other) * 100) / 100;
}

/** Format a customer + its active containers for clipboard copy. */
function formatCustomerInfo(c, { addressOnly = false, containersOnly = false } = {}) {
  const containers = (c?.containers || []).filter((ct) => (ct?.status || "active") === "active");
  const addressBlock =
    "Service Address:\n" +
    (c?.service_address ? c.service_address : "—");
  const containersBlock =
    "Containers:\n" +
    (containers.length
      ? containers
          .map((ct) => {
            const qty = Number(ct.quantity || 1);
            const size = ct.container_size != null ? `${ct.container_size} Yard` : "—";
            const stream = ct.waste_stream || "MSW";
            return `- ${qty}x ${size} ${stream}`;
          })
          .join("\n")
      : "- None");

  if (addressOnly) return addressBlock;
  if (containersOnly) return containersBlock;

  return [
    (c?.business_name || "—").toUpperCase(),
    "",
    addressBlock,
    "",
    containersBlock,
  ].join("\n");
}

/** Copy text to clipboard with a graceful fallback for non-HTTPS / older browsers. */
async function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch { /* fall through to legacy path */ }
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const FREQ_WEEK_MULT = {
  weekly: 1.0,
  biweekly: 0.5,
  monthly: 1.0 / 4.33,
  twice_weekly: 1.0,
  three_weekly: 1.0,
};
const SUSPENSION_REASONS = ["Payment", "Account Hold", "Legal", "Other"];

const emptyForm = {
  business_name: "", service_address: "", lat: "", lng: "",
  contact_name: "", phone: "", email: "",
  containers: [newContainer()],
  gate_code: "", service_notes: "", status: "active",
  group_ids: [],
  suspended: false,
  suspension_reason: null,
  suspension_note: "",
};

// `editOnlyId` embeds just the edit modal (e.g. from the Routes map pin popup).
export default function Customers({ editOnlyId = null, onEmbeddedClose, onEmbeddedSaved } = {}) {
  const { user } = useAuth();
  const isVisitor = user?.role === "visitor";
  const [items, setItems] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]); // multi-select group filter
  const [groupsManagerOpen, setGroupsManagerOpen] = useState(false);
  const [groupFilterOpen, setGroupFilterOpen] = useState(false);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [calcPreview, setCalcPreview] = useState(null);
  const [rates, setRates] = useState({ tipping_rate: 54.20, pounds_per_yard: 100 });
  const [pickerOpen, setPickerOpen] = useState(false);
  // Container-level Pick-on-Map: holds the index of the container currently
  // being picked for. `null` means the picker is closed.
  const [containerPickerIdx, setContainerPickerIdx] = useState(null);
  // Bulk selection — admins can multi-select customer rows and bulk-add them
  // to any group (e.g. broker bundle) from the action bar that appears below
  // the header. `selectedIds` is a Set for O(1) toggle.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkGroupPickerOpen, setBulkGroupPickerOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  function toggleSelect(cid) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  }
  function clearSelection() { setSelectedIds(new Set()); }
  function toggleSelectAllVisible(visibleIds) {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }
  async function bulkAddToGroup(gid) {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/customer-groups/${gid}/members`, { customer_ids: ids });
      const gname = groups.find((g) => g.id === gid)?.name || "group";
      const added = data.added || 0;
      if (added === 0) {
        toast.info(`All ${ids.length} selected customer${ids.length === 1 ? " was" : "s were"} already in "${gname}"`);
      } else if (added < ids.length) {
        toast.success(`Added ${added} customer${added === 1 ? "" : "s"} to "${gname}" (${ids.length - added} already in group)`);
      } else {
        toast.success(`Added ${added} customer${added === 1 ? "" : "s"} to "${gname}"`);
      }
      setBulkGroupPickerOpen(false);
      clearSelection();
      // Refresh customer list + group counts so badges + filter counts update.
      await Promise.all([load(), reloadGroups()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk add failed");
    } finally {
      setBulkBusy(false);
    }
  }

  async function load() {
    const [r, s, rr, gg] = await Promise.all([
      api.get("/customers"),
      api.get("/settings"),
      api.get("/routes"),
      api.get("/customer-groups"),
    ]);
    setItems(r.data);
    if (editOnlyId) {
      const target = r.data.find((c) => c.id === editOnlyId);
      if (target) openEdit(target);
      else { toast.error("Customer not found"); onEmbeddedClose?.(); }
    }
    setRoutes(rr.data || []);
    setGroups(gg.data || []);
    setRates({
      tipping_rate: Number(s.data.tipping_rate) || 54.20,
      pounds_per_yard: Number(s.data.pounds_per_yard) || 100,
    });
  }
  useEffect(() => { load(); }, []);

  async function reloadGroups() {
    try {
      const { data } = await api.get("/customer-groups");
      setGroups(data || []);
    } catch { /* noop */ }
  }

  // Lookup helpers
  const groupById = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);

  function customerContainers(c) {
    const arr = c.containers && c.containers.length ? c.containers : null;
    if (arr) return arr;
    // Legacy fallback — synthesize from old top-level fields
    return [{
      waste_stream: "MSW",
      container_size: c.dumpster_size || 0,
      quantity: 1,
      service_frequency: c.service_frequency || "weekly",
      service_days: c.service_days || [],
      route_id: null,
      monthly_price: c.monthly_price || 0,
      status: c.status || "active",
    }];
  }

  function rowMetrics(c) {
    const containers = customerContainers(c).filter((x) => (x.status || "active") === "active");
    let yards = 0, revenue = 0, unitCount = 0;
    for (const ct of containers) {
      const size = Number(ct.container_size) || 0;
      const qty = Number(ct.quantity) || 1;
      // Bi-weekly / monthly customers pick up on FEWER than the full weekly rate.
      // Applying FREQ_WEEK_MULT keeps monthly yardage, disposal cost & profit correct.
      const mult = FREQ_WEEK_MULT[ct.service_frequency || "weekly"] ?? 1.0;
      const pickups = (ct.service_days || []).length * mult;
      yards += size * qty * pickups * 4.33;
      revenue += Number(ct.monthly_price) || 0;
      unitCount += qty;
    }
    const isSuspended = !!c.suspended;
    const activeRevenue = isSuspended ? 0 : revenue;
    const pounds = yards * rates.pounds_per_yard;
    const tons = pounds / 2000;
    const disposal = tons * rates.tipping_rate;
    return {
      yards: Math.round(yards * 10) / 10,
      disposal: Math.round(disposal * 100) / 100,
      profit: Math.round((activeRevenue - disposal) * 100) / 100,
      revenue: activeRevenue,
      suspended_revenue: isSuspended ? revenue : 0,
      suspended: isSuspended,
      suspension_reason: c.suspension_reason || null,
      // Total physical dumpsters at this location — respects each service line's
      // quantity (so a single MSW line with qty=11 counts as 11 containers, not 1).
      container_count: unitCount,
      service_line_count: containers.length,
      streams: [...new Set(containers.map((x) => x.waste_stream || "MSW"))],
    };
  }

  // Live calculations preview for the modal — aggregates across all containers
  useEffect(() => {
    if (!showForm) { setCalcPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data: settings } = await api.get("/settings");
        if (cancelled) return;
        const tip = Number(settings.tipping_rate) || 54.20;
        const ppy = Number(settings.pounds_per_yard) || 100;
        let yards = 0, pickups = 0, revenue = 0;
        for (const ct of (form.containers || [])) {
          if ((ct.status || "active") !== "active") continue;
          const size = Number(ct.container_size) || 0;
          const qty = Number(ct.quantity) || 1;
          const mult = FREQ_WEEK_MULT[ct.service_frequency || "weekly"] ?? 1.0;
          const p = (ct.service_days || []).length * mult;
          pickups += p;
          yards += size * qty * p * 4.33;
          revenue += computeContainerTotal(ct);
        }
        const isSuspended = !!form.suspended;
        const activeRevenue = isSuspended ? 0 : revenue;
        const pounds = yards * ppy;
        const tons = pounds / 2000;
        const disposal = tons * tip;
        setCalcPreview({
          pickups_per_week: pickups,
          monthly_yards: Math.round(yards * 100) / 100,
          estimated_pounds: Math.round(pounds * 10) / 10,
          estimated_tons: Math.round(tons * 100) / 100,
          estimated_disposal_cost: Math.round(disposal * 100) / 100,
          revenue: activeRevenue,
          suspended: isSuspended,
          suspended_revenue: isSuspended ? revenue : 0,
          estimated_profit: Math.round((activeRevenue - disposal) * 100) / 100,
          revenue_per_yard: yards ? Math.round((activeRevenue / yards) * 100) / 100 : 0,
          tipping_rate: tip,
          pounds_per_yard: ppy,
        });
      } catch (err) {
        console.warn("settings preview load failed:", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [showForm, JSON.stringify(form.containers)]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const groupSet = new Set(selectedGroupIds);
    return items.filter((c) => {
      // Group filter — when any groups are selected, the customer must belong to AT LEAST ONE.
      if (groupSet.size > 0) {
        const ids = c.group_ids || [];
        if (!ids.some((g) => groupSet.has(g))) return false;
      }
      // Search filter
      if (t) {
        const groupNames = (c.group_ids || []).map((gid) => groupById[gid]?.name).filter(Boolean);
        const hay = [c.business_name, c.service_address, c.contact_name, c.phone, c.email, ...groupNames]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(t)) return false;
      }
      return true;
    });
  }, [items, q, selectedGroupIds, groupById]);

  // Close the group filter dropdown when clicking outside.
  useEffect(() => {
    if (!groupFilterOpen) return undefined;
    function onDocClick(e) {
      if (!e.target.closest?.("[data-testid='group-filter-wrap']")) setGroupFilterOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [groupFilterOpen]);

  // Close the bulk "Add to group" picker when clicking outside.
  useEffect(() => {
    if (!bulkGroupPickerOpen) return undefined;
    function onDocClick(e) {
      if (!e.target.closest?.("[data-testid='bulk-action-bar']")) setBulkGroupPickerOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [bulkGroupPickerOpen]);

  function closeForm() {
    setShowForm(false);
    if (editOnlyId) onEmbeddedClose?.();
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  }

  function openEdit(c) {
    setEditing(c);
    const containers = (c.containers && c.containers.length)
      ? c.containers.map((ct) => {
          // Back-fill breakdown from legacy monthly_price if the row was created
          // before itemized fees existed.
          const hasBreakdown = ct.service_price != null || ct.fuel_surcharge != null
            || ct.franchise_fee != null || (ct.other_fees && ct.other_fees.length);
          return {
            ...ct,
            route_id: ct.route_id || "",
            quantity: ct.quantity || 1,
            service_days: ct.service_days || [],
            waste_stream: ct.waste_stream || "MSW",
            service_address: ct.service_address || "",
            lat: ct.lat ?? null,
            lng: ct.lng ?? null,
            service_price: hasBreakdown ? Number(ct.service_price || 0) : Number(ct.monthly_price || 0),
            fuel_surcharge: Number(ct.fuel_surcharge || 0),
            franchise_fee: Number(ct.franchise_fee || 0),
            other_fees: (ct.other_fees || []).map((f) => ({
              description: f?.description || "",
              amount: Number(f?.amount || 0),
            })),
          };
        })
      : [{
          ...newContainer(),
          waste_stream: "MSW",
          container_size: c.dumpster_size || 4,
          service_frequency: c.service_frequency || "weekly",
          service_days: c.service_days || [],
          service_price: c.monthly_price || 0,
          monthly_price: c.monthly_price || 0,
        }];
    setForm({
      business_name: c.business_name || "",
      service_address: c.service_address || "",
      lat: c.lat ?? "",
      lng: c.lng ?? "",
      contact_name: c.contact_name || "",
      phone: c.phone || "",
      email: c.email || "",
      containers,
      gate_code: c.gate_code || "",
      service_notes: c.service_notes || "",
      status: c.status || "active",
      group_ids: c.group_ids || [],
      suspended: !!c.suspended,
      suspension_reason: c.suspension_reason || null,
      suspension_note: c.suspension_note || "",
    });
    setShowForm(true);
  }

  // Container manipulators
  function updateContainer(i, patch) {
    setForm((f) => ({
      ...f,
      containers: f.containers.map((ct, idx) => idx === i ? { ...ct, ...patch } : ct),
    }));
  }
  function addContainer() {
    setForm((f) => ({ ...f, containers: [...f.containers, newContainer()] }));
  }
  function removeContainer(i) {
    setForm((f) => ({ ...f, containers: f.containers.length > 1 ? f.containers.filter((_, idx) => idx !== i) : f.containers }));
  }
  function toggleContainerDay(i, d) {
    setForm((f) => ({
      ...f,
      containers: f.containers.map((ct, idx) => {
        if (idx !== i) return ct;
        const days = ct.service_days || [];
        return {
          ...ct,
          service_days: days.includes(d) ? days.filter((x) => x !== d) : [...days, d],
        };
      }),
    }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.containers.length) {
      toast.error("Add at least one container");
      return;
    }
    // Pin any typed-but-not-picked addresses with Google before saving.
    let geoForm = form;
    if (form.service_address?.trim() && (form.lat === "" || form.lat == null)) {
      const hit = await browserGeocode(form.service_address);
      if (hit) geoForm = { ...geoForm, lat: hit.lat, lng: hit.lng };
    }
    const geoContainers = await Promise.all(form.containers.map(async (ct) => {
      if (!ct.service_address?.trim() || (ct.lat !== "" && ct.lat != null)) return ct;
      const hit = await browserGeocode(ct.service_address);
      return hit ? { ...ct, lat: hit.lat, lng: hit.lng } : ct;
    }));
    geoForm = { ...geoForm, containers: geoContainers };
    const payload = {
      ...geoForm,
      lat: geoForm.lat === "" || geoForm.lat == null ? null : Number(geoForm.lat),
      lng: geoForm.lng === "" || geoForm.lng == null ? null : Number(geoForm.lng),
      containers: geoForm.containers.map((ct) => {
        const { _key, ...rest } = ct;  // strip client-only stable-row key
        const other_fees = (ct.other_fees || [])
          .map((f) => ({
            description: (f?.description || "").trim(),
            amount: Number(f?.amount) || 0,
          }))
          .filter((f) => f.description || f.amount);
        const service_price = Number(ct.service_price) || 0;
        const fuel_surcharge = Number(ct.fuel_surcharge) || 0;
        const franchise_fee = Number(ct.franchise_fee) || 0;
        const total = other_fees.reduce((s, f) => s + f.amount, 0)
          + service_price + fuel_surcharge + franchise_fee;
        return {
          ...rest,
          container_size: Number(ct.container_size) || 4,
          quantity: Number(ct.quantity) || 1,
          service_address: (ct.service_address || "").trim(),
          lat: ct.lat === "" || ct.lat == null ? null : Number(ct.lat),
          lng: ct.lng === "" || ct.lng == null ? null : Number(ct.lng),
          service_price,
          fuel_surcharge,
          franchise_fee,
          other_fees,
          monthly_price: Math.round(total * 100) / 100,
          route_id: ct.route_id || null,
        };
      }),
    };
    try {
      const { data } = editing
        ? await api.put(`/customers/${editing.id}`, payload)
        : await api.post("/customers", payload);
      if (data?.geocode_failed) {
        toast.warning(
          `Address could not be located automatically. Customer saved — open this customer and use "Pick on Map" to set the pin manually.`,
          { duration: 8000 }
        );
      } else if (data?.lat && data?.lng) {
        if (data.geocode_level === "exact") {
          toast.success(`${editing ? "Updated" : "Created"} — pinned at exact address`);
        } else if (data.geocode_level && data.geocode_level !== "exact") {
          toast.success(
            `${editing ? "Updated" : "Created"} — pinned at ${data.geocode_level} level. Verify the pin in Routes → Map, or edit to refine.`,
            { duration: 6000 }
          );
        } else {
          toast.success(`${editing ? "Updated" : "Created"} — pinned on map`);
        }
      } else {
        toast.success(editing ? "Customer updated" : "Customer created");
      }
      if (editOnlyId) {
        onEmbeddedSaved?.(data);
        closeForm();
        return;
      }
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  }

  const [regeocodeResult, setRegeocodeResult] = useState(null);
  const [regeocoding, setRegeocoding] = useState(false);

  async function regeocodeAll() {
    setRegeocoding(true);
    try {
      // Pin with Google in the browser (server key can't reach Google).
      const missing = items.filter((c) => c.lat == null || c.lng == null);
      const failed_customers = [];
      let updated = 0;
      for (const c of missing) {
        if (!c.service_address?.trim()) {
          failed_customers.push({ id: c.id, business_name: c.business_name, service_address: "", reason: "missing_address" });
          continue;
        }
        const hit = await browserGeocode(c.service_address);
        if (hit) {
          await api.post(`/customers/${c.id}/pin`, { lat: hit.lat, lng: hit.lng });
          updated += 1;
        } else {
          failed_customers.push({ id: c.id, business_name: c.business_name, service_address: c.service_address, reason: "not_found" });
        }
      }
      const data = { updated, failed: failed_customers.length, total: missing.length, failed_customers };
      await load();
      if (data.total === 0) {
        toast.success("All customers already have map pins");
        return;
      }
      if (data.failed === 0) {
        toast.success(`Geocoded ${data.updated} of ${data.total} customers`);
        return;
      }
      // Show the failure list so admin can fix each one
      setRegeocodeResult(data);
      toast.info(`${data.updated} pinned · ${data.failed} still need attention`);
    } catch {
      toast.error("Re-geocode failed");
    } finally {
      setRegeocoding(false);
    }
  }

  function downloadFailedCsv() {
    if (!regeocodeResult?.failed_customers?.length) return;
    const rows = [
      ["Business Name", "Service Address", "Reason"],
      ...regeocodeResult.failed_customers.map((c) => [
        c.business_name || "",
        c.service_address || "",
        c.reason || "",
      ]),
    ];
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `zmex-unpinned-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  async function del(c) {
    if (!window.confirm(`Delete ${c.business_name}?`)) return;
    await api.delete(`/customers/${c.id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div className={editOnlyId ? "" : "flex flex-col h-full"} data-testid={editOnlyId ? "customer-quick-edit" : "customers-page"}>
      {!editOnlyId && (<>
      {/* Header — stays fixed, only table below scrolls */}
      <div className="flex items-end justify-between gap-3 flex-wrap flex-shrink-0">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#5C738A]">Accounts</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-[#012340] mt-1">Customers</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C738A]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              data-testid="customers-search"
              className="pl-9 pr-3 py-2 rounded-sm bg-white border border-[#012340]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
            />
          </div>

          {/* Group filter dropdown — multi-select. Clicking outside closes it. */}
          <div className="relative" data-testid="group-filter-wrap">
            <button
              type="button"
              onClick={() => setGroupFilterOpen((v) => !v)}
              className={`px-3 py-2 rounded-sm border text-sm font-bold inline-flex items-center gap-1.5 transition-all ${
                selectedGroupIds.length > 0
                  ? "bg-[#CFAB7A] text-[#012340] border-[#CFAB7A]"
                  : "bg-white text-[#012340] border-[#012340]/20 hover:bg-[#012340]/5"
              }`}
              data-testid="group-filter-toggle"
              title="Filter customers by group"
            >
              <Tag size={14} weight="bold" />
              {selectedGroupIds.length === 0
                ? "All Groups"
                : selectedGroupIds.length === 1
                  ? (groupById[selectedGroupIds[0]]?.name || "1 group")
                  : `${selectedGroupIds.length} groups`}
              <CaretDown size={12} weight="bold" />
            </button>

            {groupFilterOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-[#012340]/20 rounded-sm shadow-lg w-72 max-h-80 overflow-y-auto" data-testid="group-filter-menu">
                <div className="px-3 py-2 border-b border-[#012340]/10 flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Filter by group</span>
                  {selectedGroupIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedGroupIds([])}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#012340] font-bold hover:underline"
                      data-testid="group-filter-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {groups.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-[#5C738A] text-center">
                    No groups yet.
                    <button
                      type="button"
                      onClick={() => { setGroupFilterOpen(false); setGroupsManagerOpen(true); }}
                      className="block mx-auto mt-2 text-[#012340] font-bold underline"
                      data-testid="group-filter-create-first"
                    >
                      Create your first group
                    </button>
                  </div>
                ) : (
                  groups.map((g) => {
                    const checked = selectedGroupIds.includes(g.id);
                    return (
                      <button
                        type="button"
                        key={g.id}
                        onClick={() => {
                          setSelectedGroupIds((cur) =>
                            cur.includes(g.id) ? cur.filter((x) => x !== g.id) : [...cur, g.id]
                          );
                        }}
                        className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#FAFAFA] ${checked ? "bg-[#CFAB7A]/10" : ""}`}
                        data-testid={`group-filter-option-${g.id}`}
                      >
                        <span className={`w-4 h-4 rounded-sm border ${checked ? "border-[#012340]" : "border-[#012340]/30"}`} style={{ background: checked ? g.color : "transparent" }} />
                        <span className="flex-1 text-sm font-semibold text-[#012340] truncate">{g.name}</span>
                        <span className="text-[10px] text-[#5C738A] font-mono">{g.customer_count}</span>
                      </button>
                    );
                  })
                )}
                <button
                  type="button"
                  onClick={() => { setGroupFilterOpen(false); setGroupsManagerOpen(true); }}
                  className="w-full px-3 py-2 text-left text-xs font-bold text-[#012340] border-t border-[#012340]/10 hover:bg-[#FAFAFA]"
                  data-testid="group-filter-manage"
                >
                  <Plus size={12} weight="bold" className="inline mr-1" /> Manage groups…
                </button>
              </div>
            )}
          </div>

          {!isVisitor && (
            <>
              <button onClick={regeocodeAll} disabled={regeocoding} className="px-3 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold text-[#012340] inline-flex items-center gap-1.5 hover:bg-[#012340]/5 disabled:opacity-50" data-testid="customer-regeocode-btn" title="Re-geocode every customer missing map coordinates">
                <MapPinIcon size={14} weight="bold" className={regeocoding ? "animate-pulse" : ""} /> {regeocoding ? "Geocoding…" : "Fix Missing Pins"}
              </button>
              <button className="btn-primary" onClick={openCreate} data-testid="customer-new-btn">
                <Plus size={16} weight="bold" className="mr-1.5" /> New
              </button>
            </>
          )}
        </div>
      </div>

      {/* Active group filter chips — show what's selected for quick removal */}
      {selectedGroupIds.length > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap flex-shrink-0" data-testid="active-group-filters">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Filtering by</span>
          {selectedGroupIds.map((gid) => {
            const g = groupById[gid];
            if (!g) return null;
            return (
              <GroupBadge
                key={gid}
                group={g}
                onRemove={(id) => setSelectedGroupIds((cur) => cur.filter((x) => x !== id))}
                testid={`active-group-${gid}`}
              />
            );
          })}
          <button
            type="button"
            onClick={() => setSelectedGroupIds([])}
            className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A] font-bold hover:text-[#012340]"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Bulk-selection action bar — appears whenever 1+ rows are checked. */}
      {selectedIds.size > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap bg-[#012340] text-white rounded-sm px-3 py-2 flex-shrink-0" data-testid="bulk-action-bar">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#CFAB7A]">
            <span className="text-white font-mono">{selectedIds.size}</span> selected
          </span>
          <div className="relative">
            <button
              type="button"
              onClick={() => setBulkGroupPickerOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold hover:brightness-105 disabled:opacity-50"
              disabled={bulkBusy || groups.length === 0}
              data-testid="bulk-add-to-group-btn"
              title={groups.length === 0 ? "Create a group first" : "Add the selected customers to a group"}
            >
              <Tag size={12} weight="bold" /> Add to group <CaretDown size={11} weight="bold" />
            </button>
            {bulkGroupPickerOpen && (
              <div className="absolute left-0 top-full mt-1 z-30 bg-white text-[#012340] border border-[#012340]/15 rounded-sm shadow-lg w-64 max-h-72 overflow-y-auto" data-testid="bulk-add-to-group-menu">
                <div className="px-3 py-2 border-b border-[#012340]/10 text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">
                  Pick a group
                </div>
                {groups.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-[#5C738A] text-center">No groups yet</div>
                ) : (
                  groups.map((g) => (
                    <button
                      type="button"
                      key={g.id}
                      onClick={() => bulkAddToGroup(g.id)}
                      disabled={bulkBusy}
                      className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#FAFAFA] disabled:opacity-50"
                      data-testid={`bulk-add-option-${g.id}`}
                    >
                      <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: g.color }} />
                      <span className="text-sm font-semibold text-[#012340] flex-1 truncate">{g.name}</span>
                      <span className="text-[10px] text-[#5C738A] font-mono">{g.customer_count}</span>
                    </button>
                  ))
                )}
                <button
                  type="button"
                  onClick={() => { setBulkGroupPickerOpen(false); setGroupsManagerOpen(true); }}
                  className="w-full px-3 py-2 text-left text-xs font-bold text-[#012340] border-t border-[#012340]/10 hover:bg-[#FAFAFA]"
                  data-testid="bulk-add-manage-groups"
                >
                  <Plus size={12} weight="bold" className="inline mr-1" /> New group…
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={clearSelection}
            className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/70 hover:text-white"
            data-testid="bulk-clear-selection"
          >
            Clear
          </button>
        </div>
      )}

      {/* Scrollable table — sticky header so columns stay visible on long lists */}
      <div className="mt-4 bg-white border border-[#012340]/10 rounded-sm flex-1 min-h-0 overflow-auto" data-testid="customers-table-scroll">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-[0.2em] text-[#5C738A] bg-white sticky top-0 z-10 shadow-[0_1px_0_0_rgba(1,35,64,0.08)]">
            <tr className="text-left">
              {!isVisitor && (
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all visible customers"
                    data-testid="customers-select-all"
                    className="w-4 h-4 rounded-sm border-[#012340]/30 accent-[#CFAB7A]"
                    checked={filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id))}
                    ref={(el) => {
                      if (!el) return;
                      const some = filtered.some((c) => selectedIds.has(c.id));
                      const all = filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));
                      el.indeterminate = some && !all;
                    }}
                    onChange={() => toggleSelectAllVisible(filtered.map((c) => c.id))}
                  />
                </th>
              )}
              <th className="px-4 py-3">Business</th>
              {!isVisitor && <th className="px-4 py-3">Address</th>}
              {!isVisitor && <th className="px-4 py-3">Groups</th>}
              {!isVisitor && <th className="px-4 py-3">Containers</th>}
              <th className="px-4 py-3">Monthly</th>
              {!isVisitor && <th className="px-4 py-3">Yards/mo</th>}
              <th className="px-4 py-3">Est. Disposal</th>
              <th className="px-4 py-3">Est. Profit</th>
              {!isVisitor && <th className="px-4 py-3">Status</th>}
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const m = rowMetrics(c);
              const customerGroups = (c.group_ids || []).map((gid) => groupById[gid]).filter(Boolean);
              const visibleGroups = customerGroups.slice(0, 2);
              const overflowGroups = customerGroups.length - visibleGroups.length;
              return (
                <tr
                  key={c.id}
                  className={`border-t border-[#012340]/5 ${selectedIds.has(c.id) ? "bg-[#CFAB7A]/10" : "even:bg-[#FAFAFA]"}`}
                  data-testid={`customer-row-${c.id}`}
                >
                  {!isVisitor && (
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${c.business_name}`}
                      data-testid={`customer-select-${c.id}`}
                      className="w-4 h-4 rounded-sm border-[#012340]/30 accent-[#CFAB7A]"
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggleSelect(c.id)}
                    />
                  </td>
                  )}
                  <td className="px-4 py-3 font-semibold text-[#012340]">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{c.business_name}</span>
                      {c.suspended && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-[#D32F2F] text-white text-[9px] font-black uppercase tracking-[0.15em]"
                          title={`Suspended: ${c.suspension_reason || "Payment"}${c.suspension_note ? " · " + c.suspension_note : ""}`}
                          data-testid={`customer-suspended-${c.id}`}
                        >
                          Suspended
                        </span>
                      )}
                    </div>
                  </td>
                  {!isVisitor && (
                  <td className="px-4 py-3 text-[#5C738A] max-w-[260px] truncate" data-testid={`customer-address-${c.id}`}>
                    {c.service_address}
                  </td>
                  )}
                  {!isVisitor && (
                  <td className="px-4 py-3" data-testid={`customer-groups-${c.id}`}>
                    {customerGroups.length === 0 ? (
                      <span className="text-[10px] text-[#5C738A]/60">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {visibleGroups.map((g) => (
                          <GroupBadge key={g.id} group={g} size="xs" />
                        ))}
                        {overflowGroups > 0 && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-[#012340]/10 text-[#012340] text-[10px] font-bold"
                            title={customerGroups.slice(2).map((g) => g.name).join(", ")}
                          >
                            +{overflowGroups}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  )}
                  {!isVisitor && (
                  <td className="px-4 py-3">
                    <div className="text-xs text-[#012340] font-semibold" data-testid={`customer-container-count-${c.id}`}>
                      {m.container_count} container{m.container_count !== 1 ? "s" : ""}
                    </div>
                    <div className="text-[10px] text-[#5C738A] font-mono">
                      {m.service_line_count} service line{m.service_line_count !== 1 ? "s" : ""}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {m.streams.map((s) => (
                        <span key={s} className="inline-flex px-1.5 py-0.5 rounded-sm bg-[#CFAB7A]/20 text-[#012340] text-[10px] font-bold">{s}</span>
                      ))}
                    </div>
                  </td>
                  )}
                  <td className="px-4 py-3 font-bold">
                    <div>${m.revenue.toLocaleString()}</div>
                    {m.suspended && m.suspended_revenue > 0 && (
                      <div className="text-[10px] font-mono font-normal text-[#D32F2F]" data-testid={`customer-suspended-revenue-${c.id}`}>
                        ${m.suspended_revenue.toFixed(2)}/mo on hold
                      </div>
                    )}
                  </td>
                  {!isVisitor && <td className="px-4 py-3 font-mono text-xs">{m.yards}</td>}
                  <td className="px-4 py-3 font-mono text-xs">${m.disposal.toFixed(2)}</td>
                  <td className={`px-4 py-3 font-mono text-xs font-bold ${m.profit >= 0 ? "text-[#2E7D32]" : "text-[#D32F2F]"}`}>${m.profit.toFixed(2)}</td>
                  {!isVisitor && (
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold ${c.status === "active" ? "bg-[#2E7D32]/10 text-[#2E7D32]" : "bg-[#D32F2F]/10 text-[#D32F2F]"}`}>
                      {c.status}
                    </span>
                  </td>
                  )}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isVisitor ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-[#5C738A]" data-testid={`customer-actions-locked-${c.id}`}>
                        <Lock size={11} weight="bold" /> View only
                      </span>
                    ) : (
                      <>
                        {c.lat && c.lng && (
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`} target="_blank" rel="noreferrer" className="inline-block p-1.5 rounded-sm hover:bg-[#012340]/5 text-[#012340]" title="Open in Google Maps" data-testid={`customer-nav-${c.id}`}>
                            <NavigationArrow size={16} weight="bold" />
                          </a>
                        )}
                        <CustomerCopyMenu customer={c} />
                        <button onClick={() => openEdit(c)} className="p-1.5 rounded-sm hover:bg-[#012340]/5" data-testid={`customer-edit-${c.id}`}>
                          <PencilSimple size={16} weight="bold" />
                        </button>
                        <button onClick={() => del(c)} className="p-1.5 rounded-sm hover:bg-[#D32F2F]/10 text-[#D32F2F]" data-testid={`customer-del-${c.id}`}>
                          <Trash size={16} weight="bold" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-[#5C738A]">No customers match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-[#5C738A] mt-2 flex-shrink-0">
        {filtered.length === items.length
          ? <>{items.length} customer{items.length === 1 ? "" : "s"}</>
          : <>{filtered.length} of {items.length} customers</>
        }
        {" · "}
        Disposal & profit columns use the current dashboard rates: <span className="font-bold">${rates.tipping_rate}/ton</span> · <span className="font-bold">{rates.pounds_per_yard} lbs/yd³</span> · 4.33 weeks/month.
      </div>
      </>)}

      <CustomerGroupsManager
        open={groupsManagerOpen}
        onClose={() => setGroupsManagerOpen(false)}
        onChanged={() => { reloadGroups(); load(); }}
      />

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={closeForm}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-white w-full max-w-2xl rounded-sm border border-[#012340]/10 max-h-[90vh] overflow-y-auto"
            data-testid="customer-form"
          >
            <div className="sticky top-0 bg-white border-b border-[#012340]/10 px-5 py-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{editing ? "Edit" : "New"}</div>
                <div className="font-heading text-xl font-bold text-[#012340] truncate">{editing && form.business_name ? form.business_name : "Customer"}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {editing && (
                  <CopyCustomerInfoButton
                    customer={{
                      business_name: form.business_name,
                      service_address: form.service_address,
                      containers: form.containers,
                    }}
                  />
                )}
                <button type="button" onClick={closeForm} className="p-2" data-testid="customer-form-close"><X size={18} weight="bold" /></button>
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Business Name" required>
                <input className={inputCls} value={form.business_name} required onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="cf-business_name" />
              </Field>
              <Field label="Status">
                <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="cf-status">
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </Field>
              <Field label="Service Address" wide required>
                <AddressAutocomplete
                  value={form.service_address}
                  onChange={(v) => setForm((f) => (v === f.service_address ? f : { ...f, service_address: v, lat: "", lng: "", address_verified: false }))}
                  onPlace={(p) =>
                    setForm((f) => ({
                      ...f,
                      service_address: p.address || f.service_address,
                      lat: p.lat ?? f.lat,
                      lng: p.lng ?? f.lng,
                      address_street: p.__partial ? f.address_street : (p.street || f.address_street || ""),
                      address_city: p.__partial ? f.address_city : (p.city || f.address_city || ""),
                      address_state: p.__partial ? f.address_state : (p.state || f.address_state || ""),
                      address_zip: p.__partial ? f.address_zip : (p.zip || f.address_zip || ""),
                      address_verified: p.verified ?? f.address_verified,
                      address_verified_at: p.verified_at ?? f.address_verified_at,
                      address_source: p.source ?? f.address_source,
                    }))
                  }
                  lat={form.lat ? Number(form.lat) : null}
                  lng={form.lng ? Number(form.lng) : null}
                  verified={form.address_verified}
                  verifiedAt={form.address_verified_at}
                  placeholder="Enter customer service address"
                  className={inputCls}
                  testid="cf-address"
                  required
                />
                <p className="text-[10px] text-[#5C738A] mt-1">Start typing and pick from suggestions, then verify the pin on the map.</p>
              </Field>
              <Field label="Latitude">
                <input className={inputCls} type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="auto" data-testid="cf-lat" />
              </Field>
              <Field label="Longitude">
                <input className={inputCls} type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="auto" data-testid="cf-lng" />
              </Field>
              <Field label="Map" wide>
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={() => setPickerOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold hover:brightness-105" data-testid="cf-pick-on-map">
                    <MapPinIcon size={14} weight="bold" /> Pick on Map
                  </button>
                  {form.lat && form.lng ? (
                    <span className="text-[11px] font-mono text-[#2E7D32]">📍 {Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}</span>
                  ) : (
                    <span className="text-[11px] text-[#5C738A]">No coordinates yet — geocoder will try on save, or click &ldquo;Pick on Map&rdquo; to set manually.</span>
                  )}
                  {(form.lat || form.lng) && (
                    <button type="button" onClick={() => setForm({ ...form, lat: "", lng: "" })} className="text-[11px] underline text-[#D32F2F]">Clear</button>
                  )}
                </div>
              </Field>
              <Field label="Contact Name"><input className={inputCls} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
              <Field label="Phone"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
              <Field label="Email" wide><input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Gate Code"><input className={inputCls} value={form.gate_code} onChange={(e) => setForm({ ...form, gate_code: e.target.value })} /></Field>
              <Field label="Service Notes" wide><textarea className={inputCls + " min-h-[60px]"} value={form.service_notes} onChange={(e) => setForm({ ...form, service_notes: e.target.value })} /></Field>

              {/* Suspension panel — flips the customer's driver-app cards to
                  a RED "SUSPENDED" skip state and forces revenue to $0 in
                  monthly totals until re-enabled. */}
              <div className="col-span-full" data-testid="suspension-panel">
                <div className={`rounded-sm border-2 p-3 transition ${form.suspended ? "border-[#D32F2F]/60 bg-[#D32F2F]/[0.08]" : "border-white/10 bg-white/[0.02]"}`}>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.suspended}
                      onChange={(e) => setForm({
                        ...form,
                        suspended: e.target.checked,
                        // default reason on first flip so it's obvious this field is required
                        suspension_reason: e.target.checked ? (form.suspension_reason || "Payment") : null,
                        suspension_note: e.target.checked ? form.suspension_note : "",
                      })}
                      className="accent-[#D32F2F] w-4 h-4"
                      data-testid="cf-suspended"
                    />
                    <span className={`font-heading font-black uppercase tracking-[0.18em] text-sm ${form.suspended ? "text-[#FF8A80]" : "text-[#5C738A]"}`}>
                      Suspend This Customer
                    </span>
                    <span className="text-[10px] text-[#5C738A] ml-auto">
                      {form.suspended ? "Drivers will see a RED skip card" : "Active — normal driver flow"}
                    </span>
                  </label>
                  {form.suspended && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mt-3">
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF8A80]">Reason</label>
                        <select
                          className={inputCls + " mt-0.5"}
                          value={form.suspension_reason || "Payment"}
                          onChange={(e) => setForm({ ...form, suspension_reason: e.target.value })}
                          data-testid="cf-suspension-reason"
                        >
                          {SUSPENSION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF8A80]">
                          Note {form.suspension_reason === "Other" ? <span className="text-[#FF8A80]/80">(required)</span> : <span className="opacity-60 lowercase">(optional)</span>}
                        </label>
                        <input
                          className={inputCls + " mt-0.5"}
                          placeholder="e.g. Invoice #4432 unpaid 45 days"
                          value={form.suspension_note || ""}
                          onChange={(e) => setForm({ ...form, suspension_note: e.target.value })}
                          data-testid="cf-suspension-note"
                        />
                      </div>
                      <div className="col-span-full text-[11px] text-[#FFB2A8]">
                        Monthly revenue set aside as <span className="font-mono">${(calcPreview?.suspended_revenue ?? 0).toFixed(2)}/mo on hold</span>. Excluded from totals until re-enabled.
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Field label="Groups" wide>
                <GroupMultiSelect
                  groups={groups}
                  selectedIds={form.group_ids || []}
                  onChange={(ids) => setForm({ ...form, group_ids: ids })}
                  onManage={() => setGroupsManagerOpen(true)}
                  testid="customer-form-groups"
                />
                <div className="text-[11px] text-[#5C738A] mt-1">
                  Assign this customer to brokers, franchises, or any custom group. Customers can belong to multiple groups.
                </div>
              </Field>
            </div>

            <div className="px-5 pb-5">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Service Lines / Waste Streams</div>
                  <div className="text-xs text-[#5C738A] mt-0.5">Add one service line per dumpster type at this location. Set <b>Quantity</b> for the number of physical containers on that line (e.g. 11 × 2 yd³ MSW). Each line can have its own schedule, route, and price.</div>
                </div>
                <button type="button" onClick={addContainer} className="px-3 py-1.5 rounded-sm bg-[#012340] text-white text-xs font-bold inline-flex items-center gap-1" data-testid="container-add">
                  <Plus size={12} weight="bold" /> Add Service Line
                </button>
              </div>
              <div className="space-y-3" data-testid="containers-list">
                {form.containers.map((ct, i) => (
                  <div key={ct.id || ct._key || `idx-${i}`} className="border border-[#012340]/15 rounded-sm p-3 bg-[#FAFAFA]" data-testid={`container-card-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-flex w-6 h-6 rounded-sm bg-[#012340] text-[#CFAB7A] grid place-items-center text-xs font-bold">{i + 1}</span>
                        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">
                          Service Line {i + 1}
                          {Number(ct.quantity) > 1 && (
                            <span className="ml-2 normal-case tracking-normal text-[#012340]" data-testid={`service-line-qty-hint-${i}`}>
                              · {Number(ct.quantity)} containers
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-1 text-[11px] font-bold text-[#012340]">
                          <input
                            type="checkbox"
                            checked={(ct.status || "active") === "active"}
                            onChange={(e) => updateContainer(i, { status: e.target.checked ? "active" : "inactive" })}
                            className="accent-[#012340]"
                            data-testid={`container-status-${i}`}
                          />
                          Active
                        </label>
                        <button type="button" onClick={() => removeContainer(i)} disabled={form.containers.length === 1} className="p-1.5 rounded-sm hover:bg-[#D32F2F]/10 text-[#D32F2F] disabled:opacity-40" data-testid={`container-remove-${i}`}>
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                      <div className="md:col-span-2">
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Waste Stream</label>
                        <select className={inputCls + " mt-0.5"} value={ct.waste_stream} onChange={(e) => updateContainer(i, { waste_stream: e.target.value })} data-testid={`container-stream-${i}`}>
                          {WASTE_STREAMS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Size</label>
                        <select className={inputCls + " mt-0.5"} value={ct.container_size} onChange={(e) => updateContainer(i, { container_size: Number(e.target.value) })} data-testid={`container-size-${i}`}>
                          {CONTAINER_SIZES.map((s) => <option key={s} value={s}>{s} yd³</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Quantity</label>
                        <input type="number" min="1" max="20" className={inputCls + " mt-0.5"} value={ct.quantity} onChange={(e) => updateContainer(i, { quantity: Number(e.target.value) || 1 })} data-testid={`container-qty-${i}`} />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Service Frequency</label>
                        <select className={inputCls + " mt-0.5"} value={ct.service_frequency} onChange={(e) => updateContainer(i, { service_frequency: e.target.value })} data-testid={`container-freq-${i}`}>
                          {FREQS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
                        </select>
                      </div>
                      {ct.service_frequency === "biweekly" && (
                        <div className="md:col-span-2">
                          <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#CFAB7A]">
                            Bi-Weekly · Last Serviced
                          </label>
                          <input
                            type="date"
                            className={inputCls + " mt-0.5"}
                            value={ct.last_serviced_at || ""}
                            onChange={(e) => updateContainer(i, { last_serviced_at: e.target.value || null })}
                            data-testid={`container-last-serviced-${i}`}
                          />
                          <div className="text-[10px] text-[#5C738A] mt-0.5">
                            Sets the rotation anchor. On the driver app, weeks with the anchor + 14, +28… are ON weeks. Off weeks show as amber "Skip" cards.
                          </div>
                        </div>
                      )}
                      <div className="md:col-span-2">
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Assigned Route</label>
                        <select className={inputCls + " mt-0.5"} value={ct.route_id || ""} onChange={(e) => updateContainer(i, { route_id: e.target.value || null })} data-testid={`container-route-${i}`}>
                          <option value="">— Unassigned —</option>
                          {routes.map((r) => (
                            <option key={r.id} value={r.id}>#{r.route_number}{r.name ? ` · ${r.name}` : ""}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2 md:col-span-4">
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">
                          Container Address <span className="opacity-60 lowercase">(optional — leave blank to use customer address)</span>
                        </label>
                        <div className="flex items-stretch gap-2 mt-0.5">
                          <input
                            type="text"
                            className={inputCls + " flex-1"}
                            placeholder="e.g. 220 Warehouse Rd, Orlando, FL — sits at a different location than the customer's main address"
                            value={ct.service_address || ""}
                            onChange={(e) => updateContainer(i, { service_address: e.target.value, lat: null, lng: null })}
                            data-testid={`container-address-${i}`}
                          />
                          <button
                            type="button"
                            onClick={() => setContainerPickerIdx(i)}
                            className="inline-flex items-center gap-1.5 px-3 rounded-sm bg-[#012340] text-white text-xs font-bold hover:brightness-110 whitespace-nowrap"
                            data-testid={`container-pick-on-map-${i}`}
                          >
                            <MapPinIcon size={13} weight="bold" /> Pick on Map
                          </button>
                        </div>
                        {ct.lat != null && ct.lng != null ? (
                          <div className="mt-1 flex items-center gap-2 text-[10px]">
                            <span className="text-[#2E7D32] font-mono">📍 {Number(ct.lat).toFixed(5)}, {Number(ct.lng).toFixed(5)}</span>
                            <button
                              type="button"
                              onClick={() => updateContainer(i, { lat: null, lng: null })}
                              className="text-[#D32F2F] underline"
                              data-testid={`container-clear-pin-${i}`}
                            >
                              Clear pin
                            </button>
                          </div>
                        ) : ct.service_address ? (
                          <div className="text-[10px] text-[#5C738A] mt-0.5">Will geocode on save — or Pick on Map to set the pin manually.</div>
                        ) : (
                          <div className="text-[10px] text-[#5C738A] mt-0.5">Inherits the customer&apos;s address / pin.</div>
                        )}
                      </div>
                      <div className="col-span-2 md:col-span-4">
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Service Days</label>
                        <div className="flex flex-wrap gap-1.5 mt-0.5">
                          {DAYS.map((d) => (
                            <button type="button" key={d} onClick={() => toggleContainerDay(i, d)} data-testid={`container-day-${i}-${d}`}
                              className={`px-3 py-1.5 rounded-sm border text-xs font-bold ${(ct.service_days || []).includes(d) ? "bg-[#012340] text-white border-[#012340]" : "bg-white text-[#012340] border-[#012340]/20"}`}>
                              {d}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="md:col-span-4 bg-[#FAFAFA] border border-[#012340]/10 rounded-sm p-3" data-testid={`container-revenue-${i}`}>
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#012340]">Monthly Revenue · Itemized</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Service Price ($)</label>
                            <input type="number" step="0.01" min="0" className={inputCls + " mt-0.5"}
                              value={ct.service_price ?? ""} onChange={(e) => updateContainer(i, { service_price: e.target.value })}
                              data-testid={`container-service-price-${i}`} />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Fuel Surcharge ($)</label>
                            <input type="number" step="0.01" min="0" className={inputCls + " mt-0.5"}
                              value={ct.fuel_surcharge ?? ""} onChange={(e) => updateContainer(i, { fuel_surcharge: e.target.value })}
                              data-testid={`container-fuel-${i}`} />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Franchise Fee ($)</label>
                            <input type="number" step="0.01" min="0" className={inputCls + " mt-0.5"}
                              value={ct.franchise_fee ?? ""} onChange={(e) => updateContainer(i, { franchise_fee: e.target.value })}
                              data-testid={`container-franchise-${i}`} />
                          </div>
                        </div>

                        {/* Other Fees repeater */}
                        <div className="mt-3">
                          <div className="flex items-center justify-between">
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Other Fees</div>
                            <button
                              type="button"
                              onClick={() => updateContainer(i, { other_fees: [...(ct.other_fees || []), { description: "", amount: 0 }] })}
                              className="text-[11px] font-bold uppercase tracking-wider text-[#012340] hover:underline"
                              data-testid={`container-add-other-fee-${i}`}
                            >
                              + Add Fee
                            </button>
                          </div>
                          {(ct.other_fees || []).length === 0 ? (
                            <div className="text-xs text-[#5C738A] italic mt-1">No other fees. Click &quot;Add Fee&quot; for Environmental, Administrative, etc.</div>
                          ) : (
                            <div className="space-y-1.5 mt-1.5">
                              {(ct.other_fees || []).map((f, fi) => (
                                <div key={fi} className="grid grid-cols-12 gap-2 items-center" data-testid={`container-other-fee-row-${i}-${fi}`}>
                                  <input
                                    type="text"
                                    placeholder="Fee name (e.g. Environmental Fee)"
                                    className={inputCls + " col-span-7"}
                                    value={f.description || ""}
                                    onChange={(e) => {
                                      const arr = [...(ct.other_fees || [])];
                                      arr[fi] = { ...arr[fi], description: e.target.value };
                                      updateContainer(i, { other_fees: arr });
                                    }}
                                    data-testid={`container-other-fee-desc-${i}-${fi}`}
                                  />
                                  <input
                                    type="number" step="0.01" min="0"
                                    placeholder="Amount"
                                    className={inputCls + " col-span-4"}
                                    value={f.amount ?? ""}
                                    onChange={(e) => {
                                      const arr = [...(ct.other_fees || [])];
                                      arr[fi] = { ...arr[fi], amount: e.target.value };
                                      updateContainer(i, { other_fees: arr });
                                    }}
                                    data-testid={`container-other-fee-amt-${i}-${fi}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const arr = (ct.other_fees || []).filter((_, idx) => idx !== fi);
                                      updateContainer(i, { other_fees: arr });
                                    }}
                                    className="col-span-1 text-[#B71C1C] font-bold text-lg leading-none hover:brightness-125"
                                    aria-label="Remove fee"
                                    data-testid={`container-other-fee-del-${i}-${fi}`}
                                  >×</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Live Monthly Total */}
                        <div className="mt-3 border-t border-[#012340]/10 pt-2 flex items-baseline justify-between" data-testid={`container-monthly-total-${i}`}>
                          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A]">Monthly Customer Total</span>
                          <span className="font-heading font-black text-lg text-[#012340]">
                            ${computeContainerTotal(ct).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                      <div className="md:col-span-4">
                        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A]">Driver Notes (this container)</label>
                        <input className={inputCls + " mt-0.5"} value={ct.driver_notes || ""} onChange={(e) => updateContainer(i, { driver_notes: e.target.value })} placeholder="e.g. Behind building, code 1234" data-testid={`container-notes-${i}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {calcPreview && (
              <div className="mx-5 mb-3 bg-[#FAFAFA] border border-[#012340]/10 rounded-sm p-4" data-testid="customer-calcs">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-2">
                  Calculations · {calcPreview.pickups_per_week} pickup{calcPreview.pickups_per_week !== 1 ? "s" : ""}/wk · {calcPreview.pounds_per_yard} lbs/yd³ · ${calcPreview.tipping_rate}/ton
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3 text-sm">
                  <Calc label="Monthly Yards" value={`${calcPreview.monthly_yards} yd³`} />
                  <Calc label="Est. Pounds" value={`${calcPreview.estimated_pounds.toLocaleString()} lbs`} />
                  <Calc label="Est. Tons" value={`${calcPreview.estimated_tons}`} />
                  <Calc label="Est. Disposal" value={`$${calcPreview.estimated_disposal_cost.toFixed(2)}`} />
                  <Calc label="Rev / Yard" value={`$${calcPreview.revenue_per_yard.toFixed(2)}`} />
                  <Calc label="Est. Profit" value={`$${calcPreview.estimated_profit.toFixed(2)}`} positive={calcPreview.estimated_profit >= 0} />
                </div>
              </div>
            )}
            {editing && (
              <div className="mx-5 mb-3">
                <CustomerBillingPanel customerId={editing.id} onLinkChange={load} />
              </div>
            )}
            {editing && (
              <div className="mx-5 mb-3">
                <PortalAccessPanel customerId={editing.id} />
              </div>
            )}
            <div className="sticky bottom-0 bg-white border-t border-[#012340]/10 px-5 py-3 flex justify-end gap-2">
              <button type="button" className="px-4 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold" onClick={closeForm} data-testid="customer-form-cancel">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="customer-save">{editing ? "Update" : "Create"}</button>
            </div>
          </form>
        </div>
      )}

      {pickerOpen && (
        <MapPickerModal
          initialLat={form.lat || null}
          initialLng={form.lng || null}
          initialAddress={form.service_address || ""}
          onClose={() => setPickerOpen(false)}
          onPick={(lat, lng) => {
            setForm((f) => ({ ...f, lat, lng }));
            setPickerOpen(false);
            toast.success(`Pin set at ${lat.toFixed(5)}, ${lng.toFixed(5)}`);
          }}
        />
      )}

      {containerPickerIdx != null && form.containers[containerPickerIdx] && (
        <MapPickerModal
          initialLat={form.containers[containerPickerIdx].lat || form.lat || null}
          initialLng={form.containers[containerPickerIdx].lng || form.lng || null}
          initialAddress={form.containers[containerPickerIdx].service_address || form.service_address || ""}
          onClose={() => setContainerPickerIdx(null)}
          onPick={(lat, lng) => {
            updateContainer(containerPickerIdx, { lat, lng });
            setContainerPickerIdx(null);
            toast.success(`Container pin set at ${lat.toFixed(5)}, ${lng.toFixed(5)}`);
          }}
        />
      )}

      {regeocodeResult && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setRegeocodeResult(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl rounded-sm border border-[#012340]/10 max-h-[85vh] flex flex-col" data-testid="regeocode-results">
            <div className="px-5 py-4 border-b border-[#012340]/10 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Bulk geocode complete</div>
                <div className="font-heading text-xl font-bold text-[#012340]">
                  <span className="text-[#2E7D32]">{regeocodeResult.updated}</span> pinned ·
                  <span className="text-[#F57C00] ml-1">{regeocodeResult.failed}</span> need attention
                </div>
              </div>
              <button onClick={() => setRegeocodeResult(null)} className="p-2 rounded-sm hover:bg-[#012340]/5"><X size={18} weight="bold" /></button>
            </div>
            <div className="px-5 py-3 border-b border-[#012340]/10 text-xs text-[#5C738A]">
              Click any row to open it and place the pin manually with Google Places suggestions or click-to-pin.
            </div>
            <div className="overflow-y-auto flex-1">
              <ul className="divide-y divide-[#012340]/5">
                {regeocodeResult.failed_customers.map((row) => {
                  const cust = items.find((c) => c.id === row.id);
                  return (
                    <li key={row.id} className="px-5 py-3 flex items-center gap-3 hover:bg-[#FAFAFA]" data-testid={`regeocode-failed-${row.id}`}>
                      <div className="w-7 h-7 rounded-sm bg-[#F57C00]/15 text-[#F57C00] grid place-items-center"><MapPinIcon size={14} weight="bold" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#012340] truncate">{row.business_name || "(no name)"}</div>
                        <div className="text-xs text-[#5C738A] truncate">{row.service_address || "(no address)"}</div>
                        <div className="text-[10px] font-mono text-[#5C738A] mt-0.5">{row.reason}</div>
                      </div>
                      <button
                        onClick={() => { if (cust) { openEdit(cust); setRegeocodeResult(null); } }}
                        disabled={!cust}
                        className="px-3 py-1.5 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold disabled:opacity-50"
                        data-testid={`regeocode-fix-${row.id}`}
                      >
                        Fix on Map
                      </button>
                    </li>
                  );
                })}
                {!regeocodeResult.failed_customers.length && (
                  <li className="px-5 py-8 text-center text-xs text-[#5C738A]">No failures to display.</li>
                )}
              </ul>
            </div>
            <div className="px-5 py-3 border-t border-[#012340]/10 flex items-center justify-between gap-2">
              <button onClick={downloadFailedCsv} className="px-3 py-2 rounded-sm border border-[#012340]/20 text-xs font-bold text-[#012340]" data-testid="regeocode-csv-btn">
                Download CSV
              </button>
              <button onClick={() => setRegeocodeResult(null)} className="btn-primary">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full rounded-sm bg-white border border-[#012340]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]";

function Field({ label, children, wide, required }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">{label}{required && <span className="text-[#D32F2F]">*</span>}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Calc({ label, value, positive }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C738A] font-bold">{label}</div>
      <div className={`font-heading font-bold mt-0.5 ${positive === false ? "text-[#D32F2F]" : positive === true ? "text-[#2E7D32]" : "text-[#012340]"}`}>{value}</div>
    </div>
  );
}

/** Compact 3-action copy menu — `Copy Customer Info` is the primary action;
 *  hovering / focusing reveals quick "Address only" and "Containers only" buttons. */
/** Prominent "Copy Customer Info" button for the customer profile modal. */
function CopyCustomerInfoButton({ customer }) {
  const [copied, setCopied] = useState(false);

  async function onClick() {
    const text = formatCustomerInfo(customer);
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      toast.success("Copied Successfully", { description: customer?.business_name || "Customer info" });
      window.setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Couldn't copy — your browser blocked clipboard access");
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold tracking-wide transition-all ${
        copied
          ? "bg-[#2E7D32] text-white"
          : "bg-[#012340] text-white hover:bg-[#012340]/90"
      }`}
      data-testid="customer-profile-copy-info"
      aria-label="Copy customer info"
    >
      <Copy size={14} weight="bold" />
      <span className="hidden sm:inline">{copied ? "Copied Successfully" : "Copy Customer Info"}</span>
      <span className="sm:hidden">{copied ? "✓ Copied" : "Copy"}</span>
    </button>
  );
}

function CustomerCopyMenu({ customer }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState("");
  const ref = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function doCopy(kind, label) {
    const text = formatCustomerInfo(customer, {
      addressOnly: kind === "address",
      containersOnly: kind === "containers",
    });
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(label);
      toast.success("Copied Successfully", { description: label });
      window.setTimeout(() => setCopied(""), 1800);
    } else {
      toast.error("Couldn't copy — your browser blocked clipboard access");
    }
  }

  return (
    <span ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => doCopy("all", "Customer info")}
        onContextMenu={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        className="p-1.5 rounded-sm hover:bg-[#012340]/5 text-[#012340] relative"
        title="Click to copy customer info · Right-click for more options"
        data-testid={`customer-copy-${customer.id}`}
      >
        <Copy size={16} weight="bold" />
        {copied && (
          <span
            className="absolute -top-7 right-0 px-2 py-0.5 rounded-sm bg-[#2E7D32] text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap shadow-md pointer-events-none"
            data-testid={`customer-copy-toast-${customer.id}`}
          >
            ✓ Copied
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="p-1.5 rounded-sm hover:bg-[#012340]/5 text-[#012340] -ml-1"
        title="More copy options"
        aria-label="Copy options"
        data-testid={`customer-copy-menu-${customer.id}`}
      >
        <span className="text-xs font-bold">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 min-w-[200px] bg-white border border-[#012340]/15 rounded-sm shadow-md text-left">
          <button
            onClick={() => { setOpen(false); doCopy("all", "Customer info"); }}
            className="w-full text-left px-3 py-2 text-xs font-bold text-[#012340] hover:bg-[#FAFAFA] flex items-center gap-2"
            data-testid={`customer-copy-all-${customer.id}`}
          >
            <Copy size={12} weight="bold" /> Copy Customer Info
          </button>
          <div className="border-t border-[#012340]/5" />
          <button
            onClick={() => { setOpen(false); doCopy("address", "Address"); }}
            className="w-full text-left px-3 py-2 text-xs text-[#012340] hover:bg-[#FAFAFA] flex items-center gap-2"
            data-testid={`customer-copy-address-${customer.id}`}
          >
            <MapPinIcon size={12} weight="bold" /> Copy Address Only
          </button>
          <button
            onClick={() => { setOpen(false); doCopy("containers", "Containers"); }}
            className="w-full text-left px-3 py-2 text-xs text-[#012340] hover:bg-[#FAFAFA] flex items-center gap-2"
            data-testid={`customer-copy-containers-${customer.id}`}
          >
            <Copy size={12} weight="bold" /> Copy Containers Only
          </button>
        </div>
      )}
    </span>
  );
}
